/**
 * Which travel figures apply to a venue, for a given region.
 *
 * A venue used to carry one set of figures, measured from whichever city it was set up against.
 * venue_region_travel now holds a set per region, and is the source of truth wherever it has a
 * row. The venue's own columns stay as the standing default for every region that has none -
 * most of them, for most venues, and probably for a long time.
 */

/** A Prisma Decimal, a number, or a numeric string - all of which these columns can arrive as. */
type DecimalLike = { toString: () => string } | number | string | null | undefined

const toNumber = (value: DecimalLike): number => {
  if (value === null || value === undefined) return 0
  const asNumber = typeof value === 'number' ? value : Number(value.toString())
  return Number.isFinite(asNumber) ? asNumber : 0
}

const toNullableInt = (value: number | null | undefined): number | null =>
  value === null || value === undefined ? null : value

export interface VenueTravelFigures {
  mileage_return?: number | null
  driver_bonus?: DecimalLike
  out_of_town_bonus_crew?: DecimalLike
  out_of_town_bonus_client?: DecimalLike
  travel_peak?: number | null
  travel_off_peak?: number | null
}

export interface VenueRegionTravelRow extends VenueTravelFigures {
  region_id: number
}

export interface ResolvedVenueTravel {
  mileage_return: number
  driver_bonus: number
  out_of_town_bonus_crew: number
  out_of_town_bonus_client: number
  travel_peak: number | null
  travel_off_peak: number | null
  /** True when these came from a per-region row rather than the venue's own columns. */
  fromRegionRow: boolean
}

/**
 * Resolve a venue's travel figures for one region.
 *
 * A matching row wins outright - every figure comes from it, including the ones that are null.
 * Falling back field by field would be worse than useless: it would hand back London's mileage
 * beside Birmingham's out-of-town bonus, a combination that was never measured and that nobody
 * could spot afterwards. Either the row answers for that region or the venue's default does.
 */
export const resolveVenueTravel = (
  venue: (VenueTravelFigures & { venue_region_travel?: VenueRegionTravelRow[] | null }) | null | undefined,
  regionId: number | null | undefined
): ResolvedVenueTravel => {
  const row =
    regionId === null || regionId === undefined
      ? undefined
      : venue?.venue_region_travel?.find((candidate) => candidate.region_id === regionId)

  const source: VenueTravelFigures = row ?? venue ?? {}

  return {
    mileage_return: toNumber(source.mileage_return),
    driver_bonus: toNumber(source.driver_bonus),
    out_of_town_bonus_crew: toNumber(source.out_of_town_bonus_crew),
    out_of_town_bonus_client: toNumber(source.out_of_town_bonus_client),
    travel_peak: toNullableInt(source.travel_peak),
    travel_off_peak: toNullableInt(source.travel_off_peak),
    fromRegionRow: Boolean(row),
  }
}
